import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { CategoryType } from 'eve-account-client';
import { Button } from '@ver-uds/react';
import { DropdownControl, Form, TextInputControl } from '@ver-uds/uswds-react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { useEmployeeRangesQuery } from '../../../services';
import CompanyInformationFormData from './CompanyInformationFormData';
import HelpModal, { HelpModalProps } from '../../HelpModal';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import NaicsCodeTable from '../../NaicsCodeTable';
import Section from '../../Section';
import DUNSField from '../../DUNSField';
import UEIField from '../../UEIField';
import FormVariant from '../FormVariant';
import MarketingQuestions from './MarketingQuestions/MarketingQuestions';
import { MarketingChannel } from './MarketingChannel';
import { CompanyInformationFormValues } from './CompanyInformationFormValues';
import LabelHint from './LabelHint';

export type CompanyInformationSubmitData = {
  companyInfoData: CompanyInformationFormData;
  marketingChannelData: MarketingChannel;
};

export interface EmployerCategoryFormProps {
  variantType: FormVariant;
  isEmployer?: boolean;
  isCorporateAdmin?: boolean;
  isAgent?: boolean;
  isChild?: boolean;
  companyInfoData?: CompanyInformationFormData;
  marketingChannelData?: MarketingChannel;
  cancelUrl?: string;
  categoryType?: CategoryType;
  onSubmitForm: (data: CompanyInformationSubmitData) => void;
}

const CompanyInformationForm = ({
  variantType,
  isEmployer,
  isCorporateAdmin,
  isAgent,
  isChild,
  companyInfoData,
  marketingChannelData,
  cancelUrl,
  categoryType,
  onSubmitForm,
}: EmployerCategoryFormProps): JSX.Element => {
  const { isLoading: employeeRangesIsLoading, data: employeeRangesData } = useEmployeeRangesQuery();

  // Help Modal setup
  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);
  function initializeNode(): React.ReactNode {
    return <></>;
  }
  const [getModalContent, setModalContent] = useState({ header: '', body: initializeNode() });

  const openModal = (content: HelpModalProps['modalContent']) => {
    setModalContent(content);
    setHelpModalIsOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const methods = useForm<CompanyInformationFormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      companyName: companyInfoData?.companyName,
      dba: companyInfoData?.dba,
      employerIdNumber: companyInfoData?.employerIdNumber,
      dunsNumber: companyInfoData?.dunsNumber,
      employeeRangeId: companyInfoData?.employeeRangeId,
      naicsCode: companyInfoData?.naicsCode,
      ueiSAM: companyInfoData?.ueiSAM,
      marketingQuestionPrimary: marketingChannelData?.primaryValue,
      marketingQuestionSecondary: marketingChannelData?.secondaryValue,
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, register, errors, reset } = methods;

  // Set the default form values when we've loaded them.
  useEffect(() => {
    reset({
      companyName: companyInfoData?.companyName,
      dba: companyInfoData?.dba,
      employerIdNumber: companyInfoData?.employerIdNumber,
      dunsNumber: companyInfoData?.dunsNumber,
      employeeRangeId: companyInfoData?.employeeRangeId,
      naicsCode: companyInfoData?.naicsCode,
      ueiSAM: companyInfoData?.ueiSAM,
      marketingQuestionPrimary: marketingChannelData?.primaryValue,
      marketingQuestionSecondary: marketingChannelData?.secondaryValue,
    });
  }, [variantType, companyInfoData, reset, marketingChannelData]);

  const handleSubmitForm = handleSubmit((data: CompanyInformationFormValues) => {
    onSubmitForm({
      companyInfoData: {
        companyName: data.companyName,
        dba: data.dba,
        employerIdNumber: data.employerIdNumber,
        dunsNumber: data.dunsNumber,
        employeeRangeId: data.employeeRangeId,
        naicsCode: data.naicsCode,
        ueiSAM: data.ueiSAM,
      },
      marketingChannelData: {
        primaryValue: data.marketingQuestionPrimary,
        secondaryValue: data.marketingQuestionSecondary,
      },
    });
  });

  return (
    <>
      {employeeRangesIsLoading ? (
        <div className="margin-top-5 margin-bottom-5">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FormProvider {...methods}>
            <Form className="margin-top-5 margin-bottom-5 maxw-none" onSubmit={handleSubmitForm} noValidate>
              <Section>
                <TextInputControl
                  id="company-name"
                  name="companyName"
                  className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
                  label="Company Name"
                  required
                  ref={register({
                    required: {
                      value: true,
                      message: 'Company Name is required.',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Please enter no more than 100 characters.',
                    },
                    pattern: {
                      value: /^[^^()|'‘’‛`]*$/,
                      message: 'This field cannot contain carets, apostrophes, parentheses, or pipes.',
                    },
                  })}
                  errorId={errors.companyName && 'company-name-error'}
                  errorMessage={errors.companyName?.message}
                />

                <TextInputControl
                  id="dba"
                  name="dba"
                  className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
                  label="Doing Business As (DBA) Name"
                  labelHint={<LabelHint openModal={openModal} label="Doing Business As (DBA) Name" />}
                  ref={register({
                    maxLength: {
                      value: 100,
                      message: 'Please enter no more than 100 characters.',
                    },
                    pattern: {
                      value: /^[^^()|'‘’‛`]*$/,
                      message: 'This field cannot contain carets, apostrophes, parentheses, or pipes.',
                    },
                  })}
                  errorId={errors.dba && 'dba-error'}
                  errorMessage={errors.dba?.message}
                />

                {isCorporateAdmin ? null : (
                  <TextInputControl
                    id="employer-id-number"
                    name="employerIdNumber"
                    className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                    label="Employer Identification Number (EIN)"
                    hint="9 digits, numbers only"
                    labelHint={<LabelHint openModal={openModal} label="Employer Identification Number (EIN)" />}
                    required
                    ref={register({
                      validate: (value: string) => {
                        if (!/^\d{9}$/.test(value)) {
                          return 'Required Employer Identification Number (EIN) must be 9 digits in length. Example: 012345678';
                        }
                        return true;
                      },
                    })}
                    errorId={errors.employerIdNumber && 'employer-id-number-error'}
                    errorMessage={errors.employerIdNumber?.message}
                  />
                )}

                <UEIField openModal={openModal} employerCategory={categoryType as CategoryType} />

                <DUNSField openModal={openModal} />

                {isCorporateAdmin ? null : (
                  <DropdownControl
                    id="employee-range-id"
                    name="employeeRangeId"
                    className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                    label="Total Number of Employees"
                    required
                    ref={register({
                      required: {
                        value: true,
                        message: 'Total Number of Employees must be selected.',
                      },
                      valueAsNumber: true,
                    })}
                    errorId={errors.employeeRangeId && 'employee-range-id-error'}
                    errorMessage={errors.employeeRangeId?.message}
                  >
                    <option value="">- Select -</option>
                    {employeeRangesData
                      ?.sort(({ id: a }, { id: b }) => a - b)
                      .map(({ id, displayName }) => (
                        <option key={id} value={id}>
                          {displayName}
                        </option>
                      ))}
                  </DropdownControl>
                )}
              </Section>
              <hr />
              {isCorporateAdmin ? null : <NaicsCodeTable label="NAICS Code" />}

              {/* Additional Information (Marketing Questions) for Enrollment */}
              {variantType === FormVariant.ENROLLMENT && (isEmployer || isCorporateAdmin || isAgent || isChild) ? (
                <MarketingQuestions
                  parentValue={marketingChannelData?.primaryValue}
                  topDivider={!isCorporateAdmin}
                  headerTitle="Additional Information"
                  questionLabel={
                    isChild
                      ? 'What is the primary reason your Company Location is enrolling in E-Verify?'
                      : 'What is the primary reason you’re enrolling in E-Verify?'
                  }
                />
              ) : null}

              {variantType === FormVariant.ENROLLMENT ? (
                <EnrollmentStepperButtons nextButtonType="submit" />
              ) : (
                <>
                  <Link to={cancelUrl || ''} className="usa-button usa-button--outline">
                    Cancel
                  </Link>
                  <Button type="submit" variant="primary">
                    Save
                  </Button>
                </>
              )}
            </Form>
          </FormProvider>
          <HelpModal
            modalContent={getModalContent}
            open={helpModalIsOpen}
            close={(): void => {
              setHelpModalIsOpen(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default CompanyInformationForm;
